import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {
  Installation,
  NotificationPreference,
  Notification,
  NotificationCategoryWithTypes,
  NotificationMessageValue,
} from '../entities';
import {mapiqApi, NoShowReaction, NotificationDto} from '@lib/infrastructure';
import {withPayloadType} from '@lib/utils';
import {handleError} from '../../infrastructure/errorHandler';
import {AppThunk, AppThunkApiConfig} from '../../createStore';
import {filterUnreadNotifications, notificationDtoToNotification, notificationToNotificationDto} from './utils';
import {CustomActionType} from './types';
import {respondToNoShowDetected} from '../meetings';

export const setInstallation = createAction('notifications/installationSet', withPayloadType<Installation>());

export const setInAppBadgeNumber = createAction('notifications/inAppBadgeNumberSet', withPayloadType<number>());

export const removeAppBadge: AppThunk = (dispatch, _getState, extraArgument) => {
  const {notifications} = extraArgument;
  notifications.removeAllDeliveredNotifications();
  notifications.setApplicationBadgeNumber(0);
  dispatch(setInAppBadgeNumber(0));
};

export const registerPushNotifications = createAsyncThunk<Installation, Installation, AppThunkApiConfig>(
  'notifications/registerPushNotifications',
  async (installation, {extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);

    await api.postInstallation({...installation});
    // For now don't use or need the returned API installation
    return installation;
  },
  {
    condition: (installation, {getState}) => {
      const {isRegisteringNotifications} = getState().data.notification;
      return installation && !!installation.deviceId && !!installation.token && !isRegisteringNotifications;
      // It's only able to register if we do have the deviceId and token, otherwise we must skip the API call.
    },
  },
);

export const deregisterPushNotifications = createAsyncThunk<void, void, AppThunkApiConfig>(
  'notifications/deregisterPushNotifications',
  async (_, {getState, extra}) => {
    const client = extra.apiClient;
    const api = mapiqApi(client);
    const {cancelAll} = extra.notifications;

    cancelAll();

    await api.deleteInstallation(getState().data.notification.installation.deviceId);
  },
  {
    condition: (_, {getState}) => {
      const {installation, isDeregisteringNotifications} = getState().data.notification;
      return installation && !!installation.deviceId && !isDeregisteringNotifications;
    },
  },
);

export const deleteNotification = createAsyncThunk<Notification, Notification, AppThunkApiConfig<Notification>>(
  'notifications/deleteNotification',
  async (notification, {dispatch, rejectWithValue, extra}) => {
    try {
      const client = extra.apiClient;
      const api = mapiqApi(client);
      await api.deleteNotification(notification.id);
      return notification;
    } catch (error) {
      handleError(error, dispatch);
      return rejectWithValue(notification);
    }
  },
);

export const loadNotificationPreferences = createAsyncThunk<NotificationPreference[], void, AppThunkApiConfig>(
  'notifications/loadNotificationPreferences',
  async (_, {extra}) => {
    const {apiClient, localStore} = extra;
    const api = mapiqApi(apiClient);

    const preferencesResponse = await api.getNotificationPreferences();

    return preferencesResponse.preferences.map((pref) => ({...pref}));
  },
  {
    condition: (_, {getState}) => {
      const {notification} = getState().data;
      return notification.preferencesLoadingStatus !== 'Loading' && !notification.isUpdatingPreferences;
    },
  },
);

export const updateNotificationPreference = createAsyncThunk<
  NotificationPreference,
  NotificationPreference,
  AppThunkApiConfig
>(
  'notifications/updateNotificationPreference',
  async (preference, {extra}) => {
    const {apiClient, localStore, notifications} = extra;
    const {requestPermissions} = notifications;
    const {setItem} = localStore;
    const api = mapiqApi(apiClient);

    // Temporarily we rely only on the push notifications for the "Updates" category
    if (preference.enabled) {
      const {alert, badge} = await requestPermissions(); // Besides requesting user's consent for notifications this also generates a new device token that is registered with the API (via notification service configure)
      if (!alert && !badge) {
        throw 'Permission denied';
      }
    }

    const preferenceResponse = await api.putNotificationPreference(preference);
    return {...preferenceResponse};
  },
  {
    condition: (_, {getState}) => {
      const {notification} = getState().data;
      return notification.preferencesLoadingStatus === 'Loaded' && !notification.isUpdatingPreferences;
    },
  },
);

export const updateNotificationPreferences = createAsyncThunk<
  NotificationPreference[],
  NotificationPreference[],
  AppThunkApiConfig
>(
  'notifications/updateNotificationPreferences',
  async (preferences, {extra}) => {
    const {apiClient, localStore, notifications} = extra;
    const {requestPermissions} = notifications;
    const {setItem} = localStore;
    const api = mapiqApi(apiClient);

    // Temporarily we rely only on the push notifications for the "Updates" category
    if (preferences.some(({type, enabled}) => type === 'PushNotification' && enabled)) {
      const {alert, badge} = await requestPermissions(); // Besides requesting user's consent for notifications this also generates a new device token that is registered with the API (via notification service configure)
      if (!alert && !badge) {
        throw 'Permission denied';
      }
    }

    const preferenceResponse = await api.patchNotificationPreference(preferences);
    return preferenceResponse;
  },
  {
    condition: (_, {getState}) => {
      const {notification} = getState().data;
      return notification.preferencesLoadingStatus === 'Loaded' && !notification.isUpdatingPreferences;
    },
  },
);

export const loadNotifications = createAsyncThunk<Notification[], void, AppThunkApiConfig>(
  'notifications/loadNotifications',
  async (_, {extra, dispatch}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    const notificationsResponse = await api.getNotifications();
    const allNotifications = notificationsResponse.map(notificationDtoToNotification);
    const unreadNotificationCount = filterUnreadNotifications(allNotifications).length;

    dispatch(setInAppBadgeNumber(unreadNotificationCount));

    return allNotifications;
  },
  {
    condition: (_, {getState}) => {
      return getState().data.notification.allNotificationsLoadingStatus !== 'Loading';
    },
  },
);

export const loadNotificationCategories = createAsyncThunk<NotificationCategoryWithTypes[], void, AppThunkApiConfig>(
  'notifications/loadNotificationCategories',
  async (_, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    return (await api.getNotificationCategories()) as NotificationCategoryWithTypes[];
  },
);

export const markNotificationsAsRead = createAsyncThunk<Notification[], Notification[], AppThunkApiConfig>(
  'notifications/markNotificationsAsRead',
  async (notificationsToRead, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);
    const allNotifications = notificationsToRead.map(notificationToNotificationDto);
    const readNotifications: NotificationDto[] = allNotifications.map((notification) => ({
      ...notification,
      isRead: true,
    }));

    if (readNotifications.length) {
      await api.patchNotifications(readNotifications);
    }
    return readNotifications.map(notificationDtoToNotification);
  },
);

export const markNotificationsCustomActionAsPerformed = createAsyncThunk<void, string, AppThunkApiConfig>(
  'notifications/markNotificationsCustomActionAsPerformed',
  async (id, {extra}) => {
    const {apiClient} = extra;
    const api = mapiqApi(apiClient);

    const markedNotification = {id, isCustomActionPerformed: true};

    await api.patchNotifications([markedNotification]);
  },
);

export const handleNotificationCustomAction = createAsyncThunk<
  void,
  {
    notificationId: string;
    type?: CustomActionType;
    notificationMessageValues: NotificationMessageValue;
  },
  AppThunkApiConfig
>(
  'notifications/handleNotificationCustomAction',
  async ({type, notificationMessageValues, notificationId}, {dispatch}) => {
    switch (type) {
      case 'NoShow__CancelRoomRemoval':
        notificationMessageValues.NoShowId &&
          (await dispatch(
            respondToNoShowDetected({
              noShowId: notificationMessageValues.NoShowId,
              response: NoShowReaction.RoomReleaseRejected,
            }),
          ).unwrap());
        break;
      case 'NoShow__ApproveRoomRemoval':
        notificationMessageValues.NoShowId &&
          (await dispatch(
            respondToNoShowDetected({
              noShowId: notificationMessageValues.NoShowId,
              response: NoShowReaction.RoomReleaseApproved,
            }),
          ).unwrap());
        break;
      default:
        break;
    }

    await dispatch(markNotificationsCustomActionAsPerformed(notificationId));
  },
);
